<template>
  <div class="app-select" :class="{ disabled, inline }">
    <app-input
      ref="input"
      @focus="e => this.$emit('focus', e)"
      :required="required"
      readonly
      :disabled="loading"
      :placeholder="placeholderValue"
      :value="label"
      :clear-button="false"
    />

    <app-modal
      @opening="onOpeningModal"
      @opened="onOpenedModal"
      @closed="onClosedModal"
      ref="modal"
      no-padding
      class="app-select-modal"
    >
      <template v-if="opened">
        <app-layout>
          <app-layout-header v-if="modalTitle || showSearch">
            <app-card>
              <h1 v-if="modalTitle" v-html="modalTitle"></h1>

              <app-input
                v-if="showSearch"
                v-model="searchQuery"
                ref="searchInput"
                type="search"
                :placeholder="searchPlaceholder"
              />
            </app-card>
          </app-layout-header>

          <app-layout-content ref="modalContent" class="negative-top-margin">
            <!-- <app-card padding="0"> -->
            <app-select-item
              v-for="item in filteredItems"
              @click="selectItem(item)"
              :key="item.key"
              v-bind="item"
              v-html="item.label"
            />
            <!-- </app-card> -->
          </app-layout-content>

          <app-layout-footer v-if="multiple">
            <app-card transparent>
              <app-buttons>
                <app-button @click="apply" primary full-width>Ok</app-button>
                <app-button @click="cancel" full-width>Cancelar</app-button>
              </app-buttons>
            </app-card>
          </app-layout-footer>
        </app-layout>
      </template>
    </app-modal>

    <div ref="defaultSlotContent" v-show="false">
      <slot />
    </div>
  </div>
</template>

<script>
import { clone, equal, sleep, naturalJoin } from '@/helpers'

export default {
  name: 'AppSelect',

  data() {
    return {
      searchQuery: '',
      opened: false,
      items: [],
      startItems: this.items,
    }
  },

  props: {
    modelValue: {
      type: [String, Number, Boolean, Array],
    },

    inline: {
      type: Boolean,
      default: false,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    required: {
      type: Boolean,
      default: false,
    },

    loadingText: {
      type: String,
      default: 'Carregando...',
    },

    searchPlaceholder: {
      type: String,
      default: 'Pesquisar...',
    },

    placeholder: {
      type: String,
    },

    emptyText: {
      type: String,
    },

    modalTitle: {
      type: String,
      default() {
        return this.placeholder
      },
    },
  },

  computed: {
    showSearch() {
      return this.items.length > 7
    },

    hasItems() {
      return this.items.length > 0
    },

    placeholderValue() {
      if (this.loading === true) return this.loadingText
      if (this.hasItems === false && this.emptyText) return this.emptyText

      return this.placeholder
    },

    label() {
      return this.stripHtml(
        naturalJoin(this.selectedItems.map(item => item.label)),
      )
    },

    filteredItems() {
      const query = this.searchQuery.toLowerCase().trim()

      let { items } = this

      if (query !== '')
        items = items.filter(item => item.label.toLowerCase().includes(query))

      return items
    },

    selectedItems() {
      return this.items.filter(item => item.selected === true)
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(newValue, oldValue) {
        if (equal(newValue, oldValue)) return

        const itemToSelect = this.getItemFromValue(newValue)

        if (itemToSelect) this.selectItem(itemToSelect)
      },
    },
  },

  created() {
    this.$on('input', value => this.$emit('change', value))
    this.$on('focus', this.openModal)
  },

  async mounted() {
    await this.loadItems()
    await this.$nextTick()

    this.observeListChanges()
  },

  methods: {
    getItemFromValue(value) {
      return this.items.find(item => equal(item.value, value))
    },

    stripHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html')

      return doc.body.textContent || ''
    },

    onKeyDown(event) {
      event.preventDefault()
      this.openModal()
    },

    async observeListChanges() {
      const observer = new MutationObserver(() =>
        this.$nextTick(this.loadItems),
      )

      let selectContent

      while (!(selectContent = this.$refs.defaultSlotContent)) await sleep(500)

      observer.observe(selectContent, {
        attributes: true,
        childList: true,
        characterData: false,
        subtree: true,
      })

      this.$once('hook:beforeUnmount', () => observer.disconnect())
    },

    async onClosedModal(options) {
      this.opened = false

      if (options?.fromWrapper === true) this.cancel()
    },

    async onOpenedModal() {
      const searchInput = this.$refs.searchInput

      if (searchInput) searchInput.focus()
    },

    async onOpeningModal() {
      this.opened = true
    },

    focus() {
      return this.$emit('focus')
    },

    selectItem(item) {
      if (this.multiple === false)
        this.selectedItems.forEach(item => (item.selected = false))

      item.selected = !item.selected

      if (this.multiple === false) this.$nextTick(this.apply)
    },

    apply() {
      let modelValue = this.selectedItems.map(item => item.value)

      if (this.multiple === false) modelValue = modelValue[0]

      this.$emit('update:modelValue', modelValue)
      this.startItems = clone(this.items)

      return this.closeModal()
    },

    cancel() {
      this.items = clone(this.startItems)

      return this.closeModal()
    },

    empty() {
      this.items = []
    },

    openModal() {
      if (this.items.length === 0) return

      this.$refs.modal.open()
    },

    closeModal() {
      return this.$refs.modal.close()
    },

    isSelectedItem(item) {
      const itemValue = this.getItemValue(item)
      const selectValue = this.modelValue

      if (Array.isArray(selectValue) === false)
        return equal(selectValue, itemValue)
      else
        return selectValue.some(selectedItem => equal(selectedItem, itemValue))
    },

    async loadItems() {
      const items = clone((this.startItems = await this.getItems()))

      this.items = items

      return items
    },

    getItem(item) {
      return {
        key: this.getItemKey(item),
        label: this.getItemLabel(item),
        value: this.getItemValue(item),
        selected: this.getItemSelected(item),
      }
    },

    getItemSelected(item) {
      return (
        item?.selected ??
        item?.props?.selected ??
        this.isSelectedItem(item) ??
        false
      )
    },

    getItemKey(item) {
      return item?.key ?? item?.props?.key
    },

    getItemLabel(item) {
      return item?.label ?? item?.props?.textContent
    },

    getItemValue(item) {
      return item?.value ?? item?.props?.value
    },

    async getItems() {
      const slots = this.$slots.default()?.[0]?.children ?? []

      return slots.map(this.getItem)
    },
  },
}
</script>

<style lang="scss" scoped>
.app-select {
  &.inline {
    display: inline-flex;
  }
}
</style>
